import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { XYPosition } from "@xyflow/react";
import { NODE_CATEGORIES } from "~/nodes/registry";
import { useGraphStore } from "~/store/graphStore";

/** Right-click either the empty canvas (add a node) or a node (clone/delete). */
export type MenuTarget =
  | { kind: "pane"; screen: XYPosition; flow: XYPosition }
  | { kind: "node"; screen: XYPosition; nodeId: string };

interface ContextMenuProps {
  target: MenuTarget;
  onClose: () => void;
}

export function ContextMenu({ target, onClose }: ContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(target.screen);
  const addNode = useGraphStore((state) => state.addNode);
  const deleteNode = useGraphStore((state) => state.deleteNode);
  const duplicateNode = useGraphStore((state) => state.duplicateNode);

  // Keep the menu on screen when the click lands near the bottom or right edge.
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const { width, height } = element.getBoundingClientRect();
    const margin = 8;

    setPosition({
      x: Math.max(
        margin,
        Math.min(target.screen.x, window.innerWidth - width - margin),
      ),
      y: Math.max(
        margin,
        Math.min(target.screen.y, window.innerHeight - height - margin),
      ),
    });
  }, [target]);

  useEffect(() => {
    const dismiss = (event: MouseEvent): void => {
      if (!ref.current?.contains(event.target as globalThis.Node)) onClose();
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      style={{ left: position.x, top: position.y }}
      className="scrollbar-slim fixed z-40 max-h-[70vh] w-52 overflow-y-auto rounded-lg border border-neutral-700 bg-panel py-1 shadow-2xl"
    >
      {target.kind === "node" ? (
        <>
          <MenuItem
            label="Clone"
            onClick={() => {
              duplicateNode(target.nodeId);
              onClose();
            }}
          />
          <MenuItem
            label="Delete"
            danger
            onClick={() => {
              deleteNode(target.nodeId);
              onClose();
            }}
          />
        </>
      ) : (
        NODE_CATEGORIES.map(({ category, definitions }) => (
          <section key={category}>
            <h3 className="px-3 pt-2 pb-1 text-[10px] font-semibold tracking-wider text-neutral-500 uppercase">
              {category}
            </h3>
            {definitions.map((definition) => (
              <MenuItem
                key={definition.type}
                label={definition.label}
                onClick={() => {
                  addNode(definition.type, target.flow);
                  onClose();
                }}
              />
            ))}
          </section>
        ))
      )}
    </div>
  );
}

function MenuItem({
  label,
  onClick,
  danger = false,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`block w-full cursor-pointer px-3 py-1.5 text-left text-sm hover:bg-panel-raised ${
        danger ? "text-rose-400" : "text-neutral-200"
      }`}
    >
      {label}
    </button>
  );
}
